/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import {
    Configuration,
    ConfigurationParameters,
    GuideApi,
    GuideRowsGetRequest,
    OneLineGuideApi,
    OneLineGuideCellsGetRequest,
} from 'client-code/grid-guide';

import { APPLICATION_FEATURE_AREA } from '../../config';
import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { DeviceModel } from '../device/DeviceModel';
import { OneLineGuideRow } from '../entities';
import { GridGuideRow } from '../entities/GridGuideRow';
import { IGridGuideServiceRepository } from '../interfaces/IGridGuideServiceRepository';
import { Log } from '../logging/Log';
import { TGridGuideRow, TOneLineGuideRow } from '../types';
import { TServicesParams } from '../types/TServicesParams';
import { DateTime } from '../utils';
import { UniqueIdentifier } from '../utils/UniqueIdentifier';

export class GridGuideServiceRepositoryImpl implements IGridGuideServiceRepository {
    private readonly TAG: string = 'GridGuideServiceRepositoryImpl';

    private readonly mParams: TServicesParams;

    public constructor(params: TServicesParams) {
        this.mParams = params;
    }

    public async fetchProgramMetadata(
        stationId: string[],
        startTime: DateTime,
        endTime: DateTime,
    ): Promise<TGridGuideRow[]> {
        try {
            if (startTime.getTime() % (30 * 60 * 1000)) {
                throw new Error('Start Time should be in nearest half hour');
            }

            if (endTime.getTime() % (30 * 60 * 1000)) {
                throw new Error('End Time should be in nearest half hour');
            }
            const startTimeInSeconds: number = Math.floor(startTime.getTime() / 1000);
            const endTimeInSeconds: number = Math.floor(endTime.getTime() / 1000);

            const commonHeaders = new CommonHeaders(APPLICATION_FEATURE_AREA.GUIDE).headers;

            const config: ConfigurationParameters = {
                basePath: this.mParams.basePath,
                apiKey: this.mParams.accessToken,
                baseOptions: {
                    timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                    headers: commonHeaders,
                },
            };

            // Sorting the stationIds lexographically and
            // returning in the same order as requested by UI
            const uniqueSortedStationIds = stationId
                .filter((id, index) => stationId.indexOf(id) === index)
                .sort();

            const requestParameters: GuideRowsGetRequest = {
                applicationName: DeviceModel.getTiVoSettings().getAppName(),
                bodyId: this.mParams.bodyId,
                originRequestId: UniqueIdentifier.generateOriginRequestId(),
                msoPartnerId: DeviceModel.getTiVoSettings().getMsoPartnerId(),
                stationId: uniqueSortedStationIds,
                windowStartTime: startTimeInSeconds,
                windowEndTime: endTimeInSeconds,
                applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
                applicationVersion: commonHeaders.ApplicationVersion,
                deviceType: commonHeaders.DeviceType,
                productName: commonHeaders.ProductName,
                whiny: this.mParams.whiny || 'false',
            };

            const requestConfiguration = new Configuration(config);
            const gridGuide = new GuideApi(requestConfiguration);
            const gridGuideResponse = await gridGuide.guideRowsGet(requestParameters);

            const gridGuideResponseMap = new Map();
            gridGuideResponse.data.forEach((guideRow) => {
                gridGuideResponseMap.set(
                    guideRow.stationId,
                    new GridGuideRow(guideRow, this.mParams.imageHostBaseUrl),
                );
            });

            const gridGuideUiResponse: Array<TGridGuideRow> = [];
            stationId.forEach((id) => {
                gridGuideUiResponse.push(gridGuideResponseMap.get(id));
            });

            return gridGuideUiResponse;
        } catch (error: any) {
            Log.e(this.TAG, 'fetchProgramMetadata()', error.message, {
                stationId,
                widowStartTime: startTime,
                windowEndTime: endTime,
            });
            throw error;
        }
    }

    public async fetchOneLineGuideRowMetadata(
        stationId: string,
        anchorTime: DateTime,
        count: number,
        offset: number,
        pastMode: boolean,
        networkCallId?: number,
    ): Promise<TOneLineGuideRow> {
        try {
            if (anchorTime.getTime() % 1800000) {
                throw new Error('Anchor time should be in nearest half hour');
            }

            if (count < 0) {
                throw new Error('Count should be positive');
            }

            if (offset < 0) {
                throw new Error('Offset should be positive');
            }

            const anchorTimeInSeconds: number = Math.floor(anchorTime.getTime() / 1000);

            const commonHeaders = new CommonHeaders(APPLICATION_FEATURE_AREA.PLAYER).headers;

            const config: ConfigurationParameters = {
                basePath: this.mParams.basePath,
                apiKey: this.mParams.accessToken,
                baseOptions: {
                    timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                    headers: commonHeaders,
                },
            };

            const requestParameters: OneLineGuideCellsGetRequest = {
                applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
                applicationName: DeviceModel.getTiVoSettings().getAppName(),
                applicationVersion: commonHeaders.ApplicationVersion,
                bodyId: this.mParams.bodyId,
                deviceType: commonHeaders.DeviceType,
                originRequestId: UniqueIdentifier.generateOriginRequestId(),
                productName: commonHeaders.ProductName,
                msoPartnerId: DeviceModel.getTiVoSettings().getMsoPartnerId(),
                stationId,
                anchorTime: anchorTimeInSeconds,
                count,
                offset,
                pastMode,
                whiny: this.mParams.whiny || 'false',
            };

            const requestConfiguration = new Configuration(config);
            const oneLineGuide = new OneLineGuideApi(requestConfiguration);
            const oneLineGuideResponse = await oneLineGuide.oneLineGuideCellsGet(requestParameters);

            const oneLineGuideRow = new OneLineGuideRow(
                oneLineGuideResponse.data,
                this.mParams.imageHostBaseUrl,
                networkCallId,
            );

            return oneLineGuideRow;
        } catch (error: any) {
            error.networkCallId = networkCallId;
            Log.e(this.TAG, 'fetchOneLineGuideRowMetadata()', error.message, {
                stationId,
                count,
                offset,
                pastMode,
            });

            throw error;
        }
    }
}
